import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { CursorPagination } from '../components/CursorPagination';
import { EmptyState, ErrorState, LoadingState, PageHeader, StatusBadge } from '../components/ui';
import { useCursorPagination } from '../hooks/useCursorPagination';
import { copy, formatDisplayName, formatModelName } from '../lib/copy';
import { adminApi } from '../lib/api';
import { formatDate } from '../lib/utils';
import { useProductScope } from '../context/ProductScopeContext';

export function AgentsPage() {
  const scope = useProductScope();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('');
  const [userId, setUserId] = useState(searchParams.get('user_id') ?? '');
  const pagination = useCursorPagination();

  useEffect(() => {
    pagination.reset();
  }, [status, userId, pagination.reset]);

  const { data, isLoading, isError, isFetching, refetch } = useQuery({
    queryKey: ['agents', status, userId, pagination.page, pagination.cursor],
    queryFn: () =>
      adminApi.agents({
        status: status || undefined,
        user_id: userId.trim() || undefined,
        cursor: pagination.cursor,
        limit: pagination.pageSize,
      }),
  });

  return (
    <div>
      <PageHeader title={copy.nav.agents} description="Последние агенты на платформе." />
      <div className="mb-4 flex flex-wrap gap-2">
        <select className="ops-input w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">{copy.agents.allStatuses}</option>
          <option value="active">active</option>
          <option value="paused">paused</option>
          <option value="draft">draft</option>
        </select>
        <input
          className="ops-input max-w-xs"
          placeholder={copy.agents.filterUser}
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
        />
      </div>
      {isLoading ? <LoadingState label="Загрузка агентов..." /> : null}
      {isError ? <ErrorState message={copy.errors.loadAgents} onRetry={() => refetch()} /> : null}
      {data && data.items.length === 0 ? <EmptyState message="Агенты не найдены." /> : null}
      {data && data.items.length > 0 ? (
        <div className="ops-card overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="ops-table">
              <thead>
                <tr>
                  <th>Название</th>
                  <th>Статус</th>
                  <th>Модель</th>
                  <th>Владелец</th>
                  <th>Сообщения</th>
                  <th>Память</th>
                  <th>Создан</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((agent) => (
                  <tr key={agent.id}>
                    <td>
                      <Link to={`/${scope}/agents/${agent.id}`} className="font-medium text-ops-text hover:underline">
                        {agent.name}
                      </Link>
                    </td>
                    <td><StatusBadge status={agent.status} kind="agent" /></td>
                    <td className="text-sm">{formatModelName(agent.model)}</td>
                    <td>
                      <Link to={`/${scope}/users/${agent.user_id}`} className="text-ops-text hover:underline">
                        {formatDisplayName(agent.user_name)}
                      </Link>
                    </td>
                    <td>{agent.messages_count}</td>
                    <td>
                      <Link to={`/${scope}/agents/${agent.id}?tab=memory`} className="hover:underline">
                        {agent.memories_count ?? 0}
                      </Link>
                    </td>
                    <td className="text-ops-muted">{formatDate(agent.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <CursorPagination
            pageNumber={pagination.pageNumber}
            itemCount={data.items.length}
            canPrev={pagination.canPrev}
            hasNext={Boolean(data.next_cursor)}
            loading={isFetching}
            onPrev={pagination.goPrev}
            onNext={() => pagination.goNext(data.next_cursor)}
          />
        </div>
      ) : null}
    </div>
  );
}
